/**
 * Edit form for an activity type.
 */
import * as React from 'react';
import { FileText } from 'lucide-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Input } from '@/components/forms/Input';
import { getActivityType, updateActivityType } from '@/services/activity-types.service';
import type { UpdateActivityTypeDto } from '@/types/activity-type.types';
import { useFormValidation } from '@/hooks/useFormValidation';
import { activityTypeValidationSchema } from '@/utils/validation-schemas-utils';
import { SlidePanelForm } from '@/components/common/SlidePanelForm';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { shouldRetryUnitGet, useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import {
  activityTypeQueryKey,
  activityTypeToFormData,
  applyActivityTypeFormField,
} from './activity-type-form-utils';

/** Props for {@link EditActivityTypeView}. */
interface EditActivityTypeViewProps {
  activityTypeId: string;
  onClose: () => void;
  onSuccess?: () => void;
}

/** Edit form for an activity type. */
export const EditActivityTypeView: React.FC<EditActivityTypeViewProps> = ({
  activityTypeId,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = React.useState<UpdateActivityTypeDto>({});
  const validation = useFormValidation(activityTypeValidationSchema);

  const {
    data: activityType,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: activityTypeQueryKey(activityTypeId),
    queryFn: () => getActivityType(activityTypeId),
    retry: shouldRetryUnitGet,
  });
  useUnitGetNotFound({
    isError,
    error,
    catalogKey: 'activityTypeNotFound',
    onClose,
    entityId: activityTypeId,
  });

  /** Hydrate the form once GET activity type resolves. */
  React.useEffect(() => {
    if (!activityType) return;
    const initialData = activityTypeToFormData(activityType);
    setFormData(initialData);
    validation.validateInitialFormData(initialData);
  }, [activityType]);

  const updateMutation = useMutation({
    mutationFn: (dto: UpdateActivityTypeDto) => updateActivityType(activityTypeId, dto),
    onSuccess: () => {
      toast.success("Type d'activité modifié avec succès !");
      onSuccess?.();
      onClose();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });

  /** Patch one field and run the shared validation schema. */
  const handleFieldChange = (
    field: keyof UpdateActivityTypeDto,
    value: string | number | undefined,
  ) => {
    const updatedData = applyActivityTypeFormField(formData, field, value);
    setFormData(updatedData);
    validation.validateField(field, value, updatedData);
    validation.markAsTouched(field);
    validation.markAsDirty(field);
  };

  const handleBlur = (field: string) => {
    validation.markAsTouched(field);
  };

  /** Validate then PATCH the activity type. */
  const handleSubmit = () => {
    if (!validation.validateForm(formData)) return;
    updateMutation.mutate(formData);
  };

  return (
    <SlidePanelForm
      formType="edit"
      title="Modifier le type d'activité"
      subtitle="Modifiez les informations du type d'activité"
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel="Enregistrer"
      isSubmitting={updateMutation.isPending}
      isValid={validation.isFormValid()}
      isLoading={isLoading || (!!activityType && Object.keys(formData).length === 0)}
    >
      <div className="form-section">
        <h4 className="form-section-title">
          <FileText className="w-4 h-4" />
          Informations générales
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Code"
            value={formData.code || ''}
            onChange={(e) => handleFieldChange('code', e.target.value)}
            onBlur={() => handleBlur('code')}
            placeholder="Ex: AIDE_DEVOIRS"
            maxLength={50}
            required
            helperText="Code unique identifiant le type"
            state={validation.getFieldState('code')}
            error={validation.getFieldError('code')}
          />

          <Input
            label="Libellé"
            value={formData.label || ''}
            onChange={(e) => handleFieldChange('label', e.target.value)}
            onBlur={() => handleBlur('label')}
            placeholder="Ex: Aide aux devoirs"
            maxLength={255}
            required
            helperText="Nom complet du type d'activité"
            state={validation.getFieldState('label')}
            error={validation.getFieldError('label')}
          />
        </div>

        <Input
          label="Description"
          value={formData.description || ''}
          onChange={(e) => handleFieldChange('description', e.target.value)}
          onBlur={() => handleBlur('description')}
          placeholder="Ex: Accompagnement aux devoirs"
          maxLength={500}
          state={validation.getFieldState('description')}
          error={validation.getFieldError('description')}
        />
      </div>
    </SlidePanelForm>
  );
};
